import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

const InvestorForm = ({
  onSubmit,
  onCancel,
  initialData = null,
  isModal = false,
  submitButtonText = "Add Investor",
  title = "Add New Investor",
}) => {
  // Get column permissions from auth context
  const { getColumnPermissions, getUserRole } = useAuth();
  const columnPermissions = getColumnPermissions();
  const userRole = getUserRole();

  // Helper to check if a field can be shown/edited
  const canShowField = (fieldKey) => {
    // Admins can see all fields
    if (userRole === 'admin') return true;
    // buyer_name is always visible (core field)
    if (fieldKey === 'buyer_name') return true;
    // For admin_team, check specific column permission
    return columnPermissions[fieldKey] === true;
  };

  const [formData, setFormData] = useState({
    buyer_name: "",
    mobile_number: "",
    email: "",
    wallet_address: "",
    purchase_date: "",
    return_date: "",
    person_place: "",
    referred_by: "",
    referrer_wallet: "",
    status: "With UNS",
    token_number: "",
    scheme_id: "",
    transaction_hash: "",
    unix_timestamp: "",
    transaction_date: "",
    from_wallet_address: "",
    to_wallet_address: "",
    usdt_received: "",
    transaction_remarks: "",
    dynamic_fields: {},
  });

  const [schemes, setSchemes] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);

  // Load initial data if editing - handle both cases: schemes loaded first or data loaded first

  useEffect(() => {
    if (initialData) {
      // Editing: prepopulate all fields from initialData
      // ...existing code...
      let parsedDynamicFields = {};
      if (initialData.dynamic_fields) {
        if (typeof initialData.dynamic_fields === "string") {
          try {
            parsedDynamicFields = JSON.parse(initialData.dynamic_fields);
          } catch (e) {
            parsedDynamicFields = {};
          }
        } else if (typeof initialData.dynamic_fields === "object") {
          parsedDynamicFields = initialData.dynamic_fields;
        }
      }
      const formDataToSet = {
        buyer_name: initialData.buyer_name || "",
        mobile_number: initialData.mobile_number || "",
        email: initialData.email || "",
        wallet_address: initialData.wallet_address || "",
        purchase_date: initialData.purchase_date
          ? initialData.purchase_date.split("T")[0]
          : "",
        return_date:
          initialData.return_date === "9999-12-31" ||
          initialData.return_date === null
            ? ""
            : initialData.return_date
            ? initialData.return_date.split("T")[0]
            : "",
        person_place: initialData.person_place || "",
        referred_by: initialData.referred_by || "",
        referrer_wallet: initialData.referrer_wallet || "",
        status: initialData.status || "With UNS",
        token_number: initialData.token_number || "",
        scheme_id: initialData.scheme_id
          ? initialData.scheme_id.toString()
          : "",
        transaction_hash: initialData.transaction_hash || "",
        unix_timestamp: initialData.unix_timestamp || "",
        transaction_date: initialData.transaction_date || "",
        from_wallet_address: initialData.from_wallet_address || "",
        to_wallet_address: initialData.to_wallet_address || "",
        usdt_received: initialData.usdt_received || "",
        transaction_remarks: initialData.transaction_remarks || "",
        dynamic_fields: parsedDynamicFields,
      };
      setFormData(formDataToSet);
      const dynamicFieldsArray = Object.entries(parsedDynamicFields).map(
        ([key, value]) => ({
          id: Date.now() + Math.random(),
          label: key,
          value: value,
        })
      );
      setDynamicFields(dynamicFieldsArray);
    } else {
      // Adding: ensure all contract stacking fields are empty
      setFormData((prev) => ({
        ...prev,
        transaction_hash: "",
        usdt_received: "",
        unix_timestamp: "",
        transaction_date: "",
        from_wallet_address: "",
        to_wallet_address: "",
        transaction_remarks: "",
      }));
    }
  }, [initialData]);

  // Fetch schemes from API
  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const response = await api.get("/api/schemes");
        setSchemes(response.data);
      } catch (error) {
        console.error("Error fetching schemes:", error);
        setSchemes([]);
      }
    };

    fetchSchemes();
  }, []);

  // Helper function to organize schemes hierarchically
  const organizeSchemes = () => {
    const parentSchemes = schemes.filter((scheme) => !scheme.parent_id);
    const childSchemes = schemes.filter((scheme) => scheme.parent_id);

    const organizedSchemes = [];

    // First, add all parent schemes in their original order (or alphabetically)
    const sortedParentSchemes = parentSchemes.sort((a, b) => {
      // Custom order for specific schemes, then alphabetical for others
      const order = {
        111: 1,
        SLL: 2,
        "10USD": 3,
        "20USD": 4,
        UNSREP: 5,
        REPNFT: 6,
        271: 7,
      };

      const aOrder = order[a.code] || 999;
      const bOrder = order[b.code] || 999;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // If both are not in the custom order, sort alphabetically
      return a.name.localeCompare(b.name);
    });

    sortedParentSchemes.forEach((parentScheme) => {
      if (parentScheme.code === "SLL") {
        // For Secondline Leader, add it as a non-selectable header
        organizedSchemes.push({
          ...parentScheme,
          isHeader: true,
          selectable: false,
        });

        // Add its sub-schemes sorted by their codes
        const subSchemes = childSchemes
          .filter((scheme) => scheme.parent_id === parentScheme.id)
          .sort((a, b) => a.code.localeCompare(b.code));

        subSchemes.forEach((subScheme) => {
          organizedSchemes.push({
            ...subScheme,
            isSubScheme: true,
            selectable: true,
          });
        });
      } else {
        // For other schemes, add them as selectable
        organizedSchemes.push({
          ...parentScheme,
          selectable: true,
        });
      }
    });

    return organizedSchemes;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If scheme is changed, clear token_number if not REPNFT or UNSREP
    if (name === "scheme_id") {
      const selectedScheme = schemes.find((s) => s.id.toString() === value);
      const isTokenRequired = selectedScheme?.requires_token || false;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Clear token_number if switching to a scheme that doesn't need it
        token_number: isTokenRequired ? prev.token_number : "",
      }));
    } else if (name === "status") {
      // Handle status change and return date logic
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // When status changes to "With UNS", clear return date and transaction fields
        // When status changes to "Returned", set return date to today if empty
        return_date:
          value === "With UNS"
            ? ""
            : value === "Returned" && !prev.return_date
            ? new Date().toISOString().split("T")[0]
            : prev.return_date,
        // Clear transaction fields when status is "With UNS"
        transaction_hash: value === "With UNS" ? "" : prev.transaction_hash,
        usdt_received: value === "With UNS" ? "" : prev.usdt_received,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Dynamic fields management
  const addDynamicField = () => {
    const newField = {
      id: Date.now() + Math.random(),
      label: "",
      value: "",
    };
    setDynamicFields([...dynamicFields, newField]);
  };

  const removeDynamicField = (id) => {
    setDynamicFields(dynamicFields.filter((field) => field.id !== id));
  };

  const updateDynamicField = (id, key, value) => {
    setDynamicFields(
      dynamicFields.map((field) =>
        field.id === id ? { ...field, [key]: value } : field
      )
    );
  };

  // Update formData.dynamic_fields whenever dynamicFields changes
  useEffect(() => {
    const dynamicFieldsObject = {};
    dynamicFields.forEach((field) => {
      if (field.label && field.label.trim()) {
        dynamicFieldsObject[field.label] = field.value;
      }
    });
    setFormData((prev) => ({ ...prev, dynamic_fields: dynamicFieldsObject }));
  }, [dynamicFields]);

  const isTokenNumberRequired = () => {
    const selectedScheme = schemes.find(
      (s) => s.id.toString() === formData.scheme_id
    );
    return selectedScheme?.requires_token || false;
  };

  const selectedScheme = schemes.find(
    (s) => s.id.toString() === formData.scheme_id
  );
  const organizedSchemesList = organizeSchemes();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    // Validation for token number
    if (isTokenNumberRequired() && !formData.token_number.trim()) {
      setMessage(`Token number is required for ${selectedScheme?.name}`);
      setLoading(false);
      return;
    }

    try {
      // Prepare form data with null values for empty fields
      const submissionData = {
        ...formData,
        email: formData.email || null,
        return_date: formData.return_date || null,
        token_number: formData.token_number || null,
        transaction_hash: formData.transaction_hash || null,
        unix_timestamp: formData.unix_timestamp || null,
        transaction_date: formData.transaction_date || null,
        from_wallet_address: formData.from_wallet_address || null,
        to_wallet_address: formData.to_wallet_address || null,
        usdt_received: formData.usdt_received || null,
        transaction_remarks: formData.transaction_remarks || null,
      };

      await onSubmit(submissionData);
      setMessage("Investor saved successfully!");

      // Reset form if not editing
      if (!initialData) {
        setFormData({
          buyer_name: "",
          mobile_number: "",
          email: "",
          wallet_address: "",
          purchase_date: "",
          return_date: "",
          person_place: "",
          referred_by: "",
          referrer_wallet: "",
          status: "With UNS",
          token_number: "",
          scheme_id: "",
          transaction_hash: "",
          unix_timestamp: "",
          transaction_date: "",
          from_wallet_address: "",
          to_wallet_address: "",
          usdt_received: "",
          transaction_remarks: "",
          dynamic_fields: {},
        });
        setDynamicFields([]);
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to save investor");
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("success")
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message}
        </div>
      )}

      {/* Personal Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buyer Person Name *
            </label>
            <input
              type="text"
              name="buyer_name"
              value={formData.buyer_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              placeholder="Enter buyer name"
            />
          </div>

          {canShowField('mobile_number') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
          )}

          {canShowField('email') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="investor@example.com"
              />
            </div>
          )}

          {canShowField('person_place') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Person Place *
              </label>
              <input
                type="text"
                name="person_place"
                value={formData.person_place}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                placeholder="City, Country"
            />
            </div>
          )}

          {canShowField('status') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="With UNS">With UNS</option>
                <option value="Returned">Returned</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Investment Information */}
      {(canShowField('scheme_name') || canShowField('token_number') || canShowField('purchase_date') || canShowField('return_date')) && (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Investment Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {canShowField('scheme_name') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Investment Scheme * (
              {schemes.filter((s) => s.parent_id || s.code !== "SLL").length}{" "}
              selectable schemes)
            </label>
            <select
              name="scheme_id"
              value={formData.scheme_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select a scheme</option>
              {organizedSchemesList.map((scheme) => (
                <option
                  key={scheme.id}
                  value={scheme.selectable ? scheme.id : ""}
                  disabled={!scheme.selectable}
                  className={`${
                    scheme.isHeader ? "font-bold text-gray-600 italic" : ""
                  } ${scheme.isSubScheme ? "pl-4" : ""}`}
                >
                  {scheme.isSubScheme ? "  └─ " : ""}
                  {scheme.name}
                  {scheme.isHeader ? " (Select sub-scheme below)" : ""}
                </option>
              ))}
            </select>
          </div>
          )}

          {canShowField('token_number') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Token Number {isTokenNumberRequired() && "*"}
              {!isTokenNumberRequired() && (
                <span className="text-sm text-gray-500 ml-1">
                  (Only for REPNFT and UNSREP schemes)
                </span>
              )}
            </label>
            <input
              type="text"
              name="token_number"
              value={formData.token_number}
              onChange={handleInputChange}
              disabled={!isTokenNumberRequired()}
              required={isTokenNumberRequired()}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isTokenNumberRequired()
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : ""
              }`}
              placeholder={
                isTokenNumberRequired()
                  ? "Enter token number"
                  : "Not required for this scheme"
              }
            />
          </div>
          )}

          {canShowField('purchase_date') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purchase Date *
            </label>
            <input
              type="date"
              name="purchase_date"
              value={formData.purchase_date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          )}

          {canShowField('return_date') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Return Date {formData.status === "Returned" && "*"}
              {formData.status === "With UNS" && (
                <span className="text-sm text-gray-500 ml-1">
                  (Not applicable - investor is with UNS)
                </span>
              )}
            </label>
            <input
              type="date"
              name="return_date"
              value={formData.return_date}
              onChange={handleInputChange}
              disabled={formData.status === "With UNS"}
              required={formData.status === "Returned"}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                formData.status === "With UNS"
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : ""
              }`}
            />
          </div>
          )}
        </div>

        {/* Transaction Information - Always visible, optional for all statuses */}
        {(canShowField('transaction_hash') || canShowField('usdt_received') || canShowField('transaction_date') || canShowField('from_wallet') || canShowField('to_wallet') || canShowField('transaction_remarks')) && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-md font-medium text-blue-900 mb-4">
            Contract Stacking Information (Optional)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {canShowField('transaction_hash') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Hash
                <span className="text-sm text-gray-500 ml-1">
                  (Blockchain transaction hash)
                </span>
              </label>
              <input
                type="text"
                name="transaction_hash"
                value={formData.transaction_hash}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter transaction hash..."
              />
            </div>
            )}

            {canShowField('usdt_received') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                USDT Received
                <span className="text-sm text-gray-500 ml-1">
                  (Amount in USDT)
                </span>
              </label>
              <input
                type="number"
                name="usdt_received"
                value={formData.usdt_received}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1000.00"
                step="0.01"
                min="0"
              />
            </div>
            )}

            {canShowField('transaction_date') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Date
                <span className="text-sm text-gray-500 ml-1">
                  (Date of transaction)
                </span>
              </label>
              <input
                type="text"
                name="transaction_date"
                value={formData.transaction_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2023-01-01"
              />
            </div>
            )}

            {canShowField('from_wallet') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Wallet Address
                <span className="text-sm text-gray-500 ml-1">
                  (Source wallet address)
                </span>
              </label>
              <input
                type="text"
                name="from_wallet_address"
                value={formData.from_wallet_address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0x..."
              />
            </div>
            )}

            {canShowField('to_wallet') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Wallet Address
                <span className="text-sm text-gray-500 ml-1">
                  (Destination wallet address)
                </span>
              </label>
              <input
                type="text"
                name="to_wallet_address"
                value={formData.to_wallet_address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0x..."
              />
            </div>
            )}

            {canShowField('transaction_remarks') && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Remarks
                <span className="text-sm text-gray-500 ml-1">
                  (Additional notes about the transaction)
                </span>
              </label>
              <textarea
                name="transaction_remarks"
                value={formData.transaction_remarks}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional notes or remarks about this transaction..."
              />
            </div>
            )}
          </div>
          <div className="mt-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  These contract stacking fields are optional and only needed if
                  you want to record blockchain transaction details. Leave empty
                  if not applicable.
                </p>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
      )}

      {/* Wallet Information */}
      {canShowField('wallet_address') && (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Wallet Information
        </h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wallet Address *
            </label>
            <input
              type="text"
              name="wallet_address"
              value={formData.wallet_address}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0x..."
              required
            />
          </div>
        </div>
      </div>
      )}

      {/* Referral Information */}
      {canShowField('referral') && (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Referral Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Referred By Person Name
            </label>
            <input
              type="text"
              name="referred_by"
              value={formData.referred_by}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Referrer name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Referrer Wallet Address
            </label>
            <input
              type="text"
              name="referrer_wallet"
              value={formData.referrer_wallet}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0x..."
            />
          </div>
        </div>
      </div>
      )}

      {/* Dynamic Fields */}
      {canShowField('additional_info') && (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Additional Information
          </h3>
          <button
            type="button"
            onClick={addDynamicField}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Field
          </button>
        </div>

        {dynamicFields.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              No additional fields added yet. Click "Add Field" to create custom
              fields.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {dynamicFields.map((field, index) => (
              <div
                key={field.id}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border"
              >
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Label
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) =>
                        updateDynamicField(field.id, "label", e.target.value)
                      }
                      placeholder="Enter field name (e.g., 'ID Number', 'Department')"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Value
                    </label>
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) =>
                        updateDynamicField(field.id, "value", e.target.value)
                      }
                      placeholder="Enter field value"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 font-mono">
                    #{index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeDynamicField(field.id)}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    title="Remove this field"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {dynamicFields.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Dynamic Fields Info
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    You have {dynamicFields.length} additional field(s). Only
                    fields with both label and value will be saved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading ? "Saving..." : submitButtonText}
        </button>
      </div>
    </form>
  );

  if (isModal) {
    return (
      <div className="mt-3">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        {formContent}
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-8">{formContent}</div>
    </div>
  );
};

export default InvestorForm;
